import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Privacy from "@/pages/Privacy";
import AppLayout from "@/components/app/AppLayout";
import Dashboard from "@/pages/Dashboard";
import Record from "@/pages/Record";
import ImportAudio from "@/pages/ImportAudio";
import TextMode from "@/pages/TextMode";
import CourseDetail from "@/pages/CourseDetail";
import Pricing from "@/pages/Pricing";
import Profile from "@/pages/Profile";
import Admin from "@/pages/Admin";
import Referral from "@/pages/Referral";
import PrintView from "@/pages/PrintView";
import PaymentResult from "@/pages/PaymentResult";
import { Loader2 } from "lucide-react";

const FullLoader = () => (
  <div className="flex min-h-screen items-center justify-center" data-testid="page-loader">
    <Loader2 className="h-6 w-6 animate-spin text-primary" />
  </div>
);

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/connexion" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  const ref = new URLSearchParams(location.search).get("parrain");
  if (ref && /^[A-Z0-9]{4,12}$/i.test(ref)) localStorage.setItem("nmc-ref", ref.toUpperCase());
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/connexion" element={<Login />} />
      <Route path="/confidentialite" element={<Privacy />} />
      <Route path="/app" element={<Protected><AppLayout /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="enregistrer" element={<Record />} />
        <Route path="importer" element={<ImportAudio />} />
        <Route path="texte" element={<TextMode />} />
        <Route path="cours/:id" element={<CourseDetail />} />
        <Route path="offres" element={<Pricing />} />
        <Route path="profil" element={<Profile />} />
        <Route path="admin" element={<Admin />} />
        <Route path="parrainage" element={<Referral />} />
      </Route>
      <Route path="/app/imprimer/:id" element={<Protected><PrintView /></Protected>} />
      <Route path="/payment/:result" element={<Protected><PaymentResult /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

const ThemedToaster = () => {
  const { theme } = useTheme();
  return <Toaster theme={theme} position="top-center" richColors closeButton />;
};

export default function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <ThemedToaster />
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}
